import { P2GButtonGroup, P2GButtonGroupItem, P2GDropdown } from '@parcel2go/core-ui';
import { useMemo } from 'react';
import { getTabbedNamedFilter } from '../../../lib/helpers/DefaultFilters';
import { FilterName } from '../../../lib/helpers/SmartSendFilterApplier';
import { iProps } from './SmartSendFilterBar';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { EventSections } from '~/apps/smartsend/lib/events/EventLogger';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';

interface props extends iProps {
  showDropdown?: boolean;
}

function ReadyNotReadyDescription(name: FilterName) {
  switch (name) {
    case 'Groupable':
      return 'Groupable ({{orders}})';
    case 'Ready':
      return 'Ready ({{orders}})';
    case 'NotReady':
      return 'Not Ready ({{orders}})';
    case 'SavedForLater':
      return 'Saved for Later ({{orders}})';
    default:
      return 'All';
  }
}

export function ReadyNotReady(props: props) {
  const { track } = useSmartSendAnalytics();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const logEvent = (e: string, v?: number) => {
    track({
      type: 'filter_changed',
      payload: GenerateBaseAnalyticsPayload([EventSections.SmartSend, 'tabs'], {
        surface: 'list-tabs',
        action: 'filter-change',
        filterName: e,
        filterValue: v
      })
    });
  };

  const selected = useMemo(() => {
    switch (props.filter.Name) {
      case 'Groupable':
      case 'Ready':
      case 'NotReady':
      case 'Issues':
      case 'SavedForLater':
        return props.filter.Name;
      default:
        return 'All';
    }
  }, [props.filter]);

  if (selected === 'Issues') {
    return null;
  }

  if (props.showDropdown) {
    return (
      <P2GDropdown
        options={[
          { value: 'All', text: t('FILTER_ALL_LABEL', { defaultValue: ReadyNotReadyDescription(null) }) },
          {
            value: 'Ready',
            text: t('FILTER_READY_LABEL', {
              defaultValue: ReadyNotReadyDescription('Ready'),
              orders: props.summary.stats.find((x) => x.key === 'Ready')?.value || 0
            })
          },
          {
            value: 'NotReady',
            text: t('FILTER_NOTREADY_LABEL', {
              defaultValue: ReadyNotReadyDescription('NotReady'),
              orders: props.summary.stats.find((x) => x.key === 'NotReady')?.value || 0
            })
          },
          {
            value: 'SavedForLater',
            text: t('FILTER_SAVEDFORLATER_LABEL', {
              defaultValue: ReadyNotReadyDescription('SavedForLater'),
              orders: props.summary.stats.find((x) => x.key === 'SavedForLater')?.value || 0
            })
          },
          {
            value: 'Groupable',
            text: t('FILTER_GROUPABLE_LABEL', {
              defaultValue: ReadyNotReadyDescription('Groupable'),
              orders: props.summary.stats.find((x) => x.key === 'Groupable')?.value || 0
            })
          }
        ]}
        id="View"
        label="Quick Filter"
        onChange={(x: string) => {
          switch (x) {
            case 'Groupable':
            case 'Ready':
            case 'SavedForLater':
            case 'NotReady':
              props.update(getTabbedNamedFilter(x, props.filter));
              break;
            default:
              props.update(getTabbedNamedFilter('DEFAULT', props.filter));
              break;
          }
          logEvent(x);
        }}
        value={selected}
      />
    );
  }

  return (
    <P2GButtonGroup sx={{ whiteSpace: 'nowrap' }}>
      <P2GButtonGroupItem
        classNames="sold-tab-all"
        selected={selected}
        value="All"
        onClick={(e) => {
          logEvent(e);
          props.update(getTabbedNamedFilter('DEFAULT', props.filter));
        }}
      >
        {t('FILTER_ALL_LABEL', { defaultValue: ReadyNotReadyDescription(null) })}
      </P2GButtonGroupItem>
      <P2GButtonGroupItem
        classNames="sold-tab-ready"
        selected={selected}
        value="Ready"
        onClick={(e) => {
          logEvent(e, (props.summary.stats.find((x) => x.key === 'Ready')?.value || 0) as number);
          props.update(getTabbedNamedFilter('Ready', props.filter));
        }}
      >
        {t('FILTER_READY_LABEL', {
          defaultValue: ReadyNotReadyDescription('Ready'),
          orders: props.summary.stats.find((x) => x.key === 'Ready')?.value || 0
        })}
      </P2GButtonGroupItem>
      <P2GButtonGroupItem
        classNames="sold-tab-notready"
        selected={selected}
        value="NotReady"
        onClick={(e) => {
          logEvent(e, (props.summary.stats.find((x) => x.key === 'NotReady')?.value || 0) as number);
          props.update(getTabbedNamedFilter('NotReady', props.filter));
        }}
      >
        {t('FILTER_NOTREADY_LABEL', {
          defaultValue: ReadyNotReadyDescription('NotReady'),
          orders: props.summary.stats.find((x) => x.key === 'NotReady')?.value || 0
        })}
      </P2GButtonGroupItem>
      <P2GButtonGroupItem
        classNames="sold-tab-savedforlater"
        selected={selected}
        value="SavedForLater"
        onClick={(e) => {
          logEvent(e, (props.summary.stats.find((x) => x.key === 'SavedForLater')?.value || 0) as number);
          props.update(getTabbedNamedFilter('SavedForLater', props.filter));
        }}
      >
        {t('FILTER_SAVEDFORLATER_LABEL', {
          defaultValue: ReadyNotReadyDescription('SavedForLater'),
          orders: props.summary.stats.find((x) => x.key === 'SavedForLater')?.value || 0
        })}
      </P2GButtonGroupItem>
      <P2GButtonGroupItem
        classNames="sold-tab-groupable"
        onClick={(e) => {
          logEvent(e, (props.summary.stats.find((x) => x.key === 'Groupable')?.value || 0) as number);
          props.update(getTabbedNamedFilter('Groupable', props.filter));
        }}
        selected={selected}
        value="Groupable"
      >
        {t('FILTER_GROUPABLE_LABEL', {
          defaultValue: ReadyNotReadyDescription('Groupable'),
          orders: props.summary.stats.find((x) => x.key === 'Groupable')?.value || 0
        })}
      </P2GButtonGroupItem>
    </P2GButtonGroup>
  );
}
